import React, { Children, Component, createContext, Fragment, useContext, useRef, useImperativeHandle } from "react";
import styled from "styled-components";

import { Popover } from "@material-ui/core";

import getLogger from "js/core/logger";
import { stopPropagation } from "js/core/utilities/stopPropagation";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { Gap5 } from "js/react/components/Gap";
import { themeColors } from "js/react/sharedStyles";

import { PropertyPanelContainer, PropertySectionList } from "../EditorComponents/PropertyPanel";
import { Icon } from "./Icon";
import { ToolTip } from "./ToolTip";

const logger = getLogger();

const Container = styled.div`
    opacity: ${props => props.disabled ? 0.5 : 1};
    align-items: center;
    cursor: ${props => props.disableOpenOnClick ? "default" : "pointer"};
    pointer-events: auto;
    min-height: 30px;
    background: none;
    border: none;
    color: currentColor;
    display: flex;
    padding: 0px;

    &.contained {
        background: white;
        border: solid 1px #ccc;
        padding: ${props => props.showArrow ? "2px 5px 3px 10px" : "5px"};

        ${props => !props.disableHoverState ? `
            &:hover {
                background: ${themeColors.rollover};
            }
        ` : ""}
        
        label {
            color: #333;
        }
    }
    
    .MuiMenuItem-root {
        padding: 4px 0px !important;
    }

    label {
        cursor: pointer;
        font-family: "Source Sans Pro";
        font-size: 13px;
        font-weight: 600;
    }

    .drop-down-arrow {
        .bai-icon {
            font-size: 24px !important;
        }
    }
    
    

    button.MuiButtonBase-root > span.MuiButton-label {
        color: white;
    }
`;

export const PopupContainer = styled.div`
    display: grid;
    grid-auto-rows: max-content;
    grid-gap: 0px;
    width: ${({ width }) => width ? `${width}px` : "auto"};
    
    & > div {
        padding: 15px;
    }
    
    & > label {
        padding: 10px 20px;
    }

    & > button {
        margin: 10px;
    }

    label {
        color: #333;
        font-size: 12px;
        text-transform: uppercase;
        font-weight: 600;
        letter-spacing: 0.03em;
    }

    hr {
        background: #ddd;
        width: 100%;
        height: 1px;
        border: none;
    }
`;

const StyledPopupContent = styled.div`
    width: ${props => (props.width + "px") ?? "auto"};
    
    ${PropertyPanelContainer} {
        padding: 0px;
        background: none !important;
    }

    ${PropertySectionList} {
        padding: 15px 20px;
    }

    .MuiMenuItem-root {
        padding: 15px 20px 15px 15px;
        font-family: "Source Sans Pro";
        font-size: 13px;
        font-weight: 600;

        .icon {
            margin-right: 6px;
            color: rgb(119, 119, 119);
            font-size: 20px;
            vertical-align: -6px;
        }
    }

    .MuiFormControlLabel-label {
        font-family: "Source Sans Pro";
        font-size: 13px;
        font-weight: 600;
        text-transform: none;
    }
`;

const popupContext = createContext();

export function PopupPreview({ children }) {
    const closePopup = useContext(popupContext);

    return typeof children === "function"
        ? children(closePopup)
        : (children ?? null);
}

export function PopupContent({ children, width = null, allowFocus = false }) {
    const closePopup = useContext(popupContext);
    const shouldStopPropagationRef = useRef(true);

    const handleMouseEvent = event => {
        const { type, target } = event;
        // Special case for MUI sliders and
        const isSliderThumb = type === "mousedown" && target?.classList?.contains("MuiSlider-thumb");
        // Special case for color pickers that need uninterrupted mouse drag events.
        const isColorPickerDrag = (type === "mousemove" || type === "mousedown") && (
            target?.closest(".chrome-picker") || target?.closest(".bai-custom-color-picker")
        );

        if (shouldStopPropagationRef.current) {
            stopPropagation()(event);
        }

        if (isSliderThumb || isColorPickerDrag) {
            shouldStopPropagationRef.current = false;
            return;
        }

        if (type === "mouseup" && !shouldStopPropagationRef.current) {
            shouldStopPropagationRef.current = true;
        }

        if (type === "mousedown" && !allowFocus) {
            // Prevent focus change
            event.preventDefault();
            event.nativeEvent.preventDefault();
        }
    };

    return (
        <StyledPopupContent
            onMouseDown={handleMouseEvent}
            onMouseMove={handleMouseEvent}
            onMouseUp={handleMouseEvent}
            onClick={handleMouseEvent}
            width={width}
        >
            {
                typeof children === "function"
                    ? children(closePopup)
                    : (children ?? null)
            }
        </StyledPopupContent>
    );
}

class PopupComponent extends Component {
    constructor(props) {
        super(props);

        this.ref = React.createRef();
        this.state = {
            show: false,
            anchorEl: null,
        };

        // Set up the forwarded ref to point to this component instance
        if (props.forwardedRef) {
            props.forwardedRef.current = this;
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { onOpenStateChange = () => { } } = this.props;
        if (prevState.anchorEl !== this.state.anchorEl) {
            onOpenStateChange(!!this.state.anchorEl);
        }
    }

    showPopup = () => {
        if (this.props.disabled) return;

        PresentationEditorController.setPopupState(true);
        // Workaround - without it, the first popup of the session appears in the wrong place
        setTimeout(() => {
            PresentationEditorController.setPopupState(true);
        }, 1);
        this.setState({
            show: true,
            anchorEl: this.ref.current,
        });

        this.props.onShow?.();
    };

    closePopup = callback => {
        this.setState({ anchorEl: null, show: false });
        PresentationEditorController.setPopupState(false);
        this.props.onClose?.();

        if (callback) callback();
    };

    updatePopupPosition = () => {
        this.setState({ anchorEl: null }, () => {
            this.setState({ anchorEl: this.ref.current });
        });
    };

    getPopupPosition = () => {
        const { position = "below", anchorPosition } = this.props;

        if (anchorPosition) {
            return {
                anchorReference: "anchorPosition",
                anchorPosition: { top: anchorPosition.top, left: anchorPosition.left }
            };
        }

        if (typeof position === "object") {
            return position;
        }

        switch (position) {
            case "above":
                return {
                    anchorReference: "anchorEl",
                    anchorOrigin: { vertical: "top", horizontal: "center" },
                    transformOrigin: { vertical: "bottom", horizontal: "center" }
                };
            case "right":
                return {
                    anchorReference: "anchorEl",
                    anchorOrigin: { vertical: "center", horizontal: "right" },
                    transformOrigin: { vertical: "center", horizontal: "left" }
                };
            case "below":
                return {
                    anchorReference: "anchorEl",
                    anchorOrigin: { vertical: "bottom", horizontal: "center" },
                    transformOrigin: { vertical: "top", horizontal: "center" }
                };
            case "left":
                return {
                    anchorReference: "anchorEl",
                    anchorOrigin: { vertical: "center", horizontal: "left" },
                    transformOrigin: { vertical: "center", horizontal: "right" }
                };
        }
    };

    handlePreviewMouseDown = event => {
        // Prevent focus change
        event.preventDefault();
        event.nativeEvent.preventDefault();

        this.props.onPreviewMouseDown?.(event);
    };

    render() {
        const {
            icon,
            fill = false,
            label,
            showArrow = false,
            contained,
            children,
            tooltip,
            disabled = false,
            disabledTooltip,
            disableOpenOnClick = false,
            disableHoverState = false,
            overrideContainerStyles = {},
            overrideLabelStyles = {},
            onPopoverMouseDown = () => { },
            id,
            PaperProps = {},
        } = this.props;
        const { anchorEl } = this.state;

        const childrenAsArray = Children.toArray(children);
        const Preview = childrenAsArray.find(child => child.type === PopupPreview) ?? null;
        const Content = childrenAsArray.find(child => child.type === PopupContent) ?? null;

        if (!Content) {
            logger.warn("Popup was rendered without PopupContent");
        }

        const { Provider } = popupContext;

        return (
            <Fragment>
                <ToolTip
                    title={disabled ? disabledTooltip : tooltip}
                    placement="bottom"
                >
                    <Container
                        id={id}
                        ref={this.ref}
                        className={`bai-popup ${contained ? "contained" : ""} ${showArrow ? "showArrow" : ""} ${showArrow ? "showArrow" : ""}`}
                        onClick={e => !disableOpenOnClick && stopPropagation(this.showPopup)(e)}
                        onMouseDown={e => !disableOpenOnClick && this.handlePreviewMouseDown(e)}
                        showArrow={showArrow}
                        disabled={disabled}
                        disableOpenOnClick={disableOpenOnClick}
                        disableHoverState={disableHoverState}
                        style={{
                            ...overrideContainerStyles
                        }}
                    >
                        {icon && <Icon fill={fill}>{icon}</Icon>}
                        {icon && label && <Gap5 />}
                        {label && <label style={{ ...overrideLabelStyles }}>{label}</label>}
                        {Preview}
                        {showArrow && <Icon className="drop-down-arrow">arrow_drop_down</Icon>}
                    </Container>
                </ToolTip>

                <Popover
                    id="id"
                    open={!!anchorEl}
                    anchorEl={anchorEl}
                    disableEnforceFocus
                    disableAutoFocus
                    disableRestoreFocus
                    onClose={() => this.closePopup()}
                    {...this.getPopupPosition()}
                    onMouseDown={onPopoverMouseDown}
                    PaperProps={PaperProps}
                >
                    <Provider value={this.closePopup}>
                        {Content}
                    </Provider>
                </Popover>
            </Fragment>
        );
    }
}

export const Popup = React.forwardRef((props, ref) => (
    <PopupComponent {...props} forwardedRef={ref} />
));

export class MenuPopup extends Component {
    render() {
        const { label, icon, fill, showArrow = true, children, onSelect, closePopupOnSelect = true, id, onShow = () => { }, tooltip } = this.props;

        let popup = (
            <Popup label={label} showArrow={showArrow} icon={icon} fill={fill} id={id} onShow={onShow}>
                <PopupContent>
                    {closePopup => (
                        React.Children.map(children, child => {
                            if (child) {
                                let childClick = child.props.onClick;
                                return React.cloneElement(child, {
                                    onClick: () => {
                                        closePopupOnSelect && closePopup();
                                        onSelect && onSelect(child.props.value);
                                        childClick && childClick();
                                    }
                                });
                            }
                        })
                    )}
                </PopupContent>
            </Popup>
        );

        if (tooltip) {
            return (
                <ToolTip title={tooltip} placement="bottom">
                    {popup}
                </ToolTip>
            );
        } else {
            return popup;
        }
    }
}

export const PanelButtonContainer = styled.div`
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background: #197cab;
    cursor: pointer;
    gap: 0px;
`;
export const PanelButton = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0px 10px;

    label {
        font-size: 9px;
        font-weight: bold;
        text-transform: uppercase;
        color: white;
        letter-spacing: .02em;
    }
    
    &:hover {
        background: rgba(0, 0, 0, 0.1);
    }
`;
